import TemplateDialog from "./dialogs/TemplateDialog";
import SaveTemplateDialog from "./dialogs/SaveTemplateDialog";
import AuthRequiredModal from "./dialogs/AuthRequiredModal";
import RenameDialog from "./dialogs/RenameDialog";

import ConfirmDialog from "./dialogs/ConfirmDialog";
import KeyboardShortcutsDialog from "./dialogs/KeyboardShortcutsDialog"; // Import KeyboardShortcutsDialog
import { AnimatePresence } from "framer-motion"; // Import AnimatePresence
import LoadProjectDialog from "./dialogs/LoadProjectDialog";
import SaveAsDialog from "./dialogs/SaveAsDialog";
import ShareProjectDialog from "./dialogs/ShareProjectDialog";
import { Project } from "../types";

interface ProjectMenuDialogsProps {
  project: Project | null;
  // Dialog visibility states
  showAuthRequiredModal: boolean;
  showTemplateDialog: boolean;
  showSaveTemplateDialog: boolean;
  showLoadProjectDialog: boolean;
  showRenameDialog: boolean;
  showSaveAsDialog: boolean; // Changed from showDuplicateDialog
  showCloseProjectDialog: boolean;
  showDeleteProjectDialog: boolean;
  showShareDialog: boolean;
  showNewProjectConfirmDialog: boolean; // Added confirm dialog state
  showKeyboardShortcutsDialog: boolean; // Added KeyboardShortcutsDialog state
  // Dialog close handlers
  setShowAuthRequiredModal: (show: boolean) => void;
  setShowTemplateDialog: (show: boolean) => void;
  setShowSaveTemplateDialog: (show: boolean) => void;
  setShowLoadProjectDialog: (show: boolean) => void;
  setShowRenameDialog: (show: boolean) => void;
  setShowSaveAsDialog: (show: boolean) => void; // Changed from setShowDuplicateDialog
  setShowCloseProjectDialog: (show: boolean) => void;
  setShowDeleteProjectDialog: (show: boolean) => void;
  setShowShareDialog: (show: boolean) => void;
  setShowNewProjectConfirmDialog: (show: boolean) => void; // Added confirm dialog setter
  setShowKeyboardShortcutsDialog: (show: boolean) => void; // Added KeyboardShortcutsDialog setter
  // Handlers needed by dialogs

  handleNewProject: () => void; // Now used by the *confirm* dialog's "Don't Save" option
  handleDelete: () => Promise<void>;
  proceedWithNewProject: () => void; // Added handler for "Don't Save"
  saveAndProceedWithNewProject: () => Promise<void>; // Added handler for "Save Changes"
  onSaveSuccess: () => void;
}

export default function ProjectMenuDialogs({
  project,
  showAuthRequiredModal,
  showTemplateDialog,
  showSaveTemplateDialog,
  showLoadProjectDialog,
  showRenameDialog,
  showSaveAsDialog, // Changed from showDuplicateDialog
  showCloseProjectDialog,
  showDeleteProjectDialog,
  showShareDialog,
  showNewProjectConfirmDialog, // Added state
  showKeyboardShortcutsDialog, // Added state
  setShowAuthRequiredModal,
  setShowTemplateDialog,
  setShowSaveTemplateDialog,
  setShowLoadProjectDialog,
  setShowRenameDialog,
  setShowSaveAsDialog, // Changed from setShowDuplicateDialog
  setShowCloseProjectDialog,
  setShowDeleteProjectDialog,
  setShowShareDialog,
  setShowNewProjectConfirmDialog, // Added setter
  setShowKeyboardShortcutsDialog, // Added setter

  handleNewProject, // Keep this, it's used by the old CloseProjectDialog confirm
  handleDelete,
  proceedWithNewProject, // Added handler
  saveAndProceedWithNewProject, // Added handler
  onSaveSuccess, // Destructure the new prop
}: ProjectMenuDialogsProps) {
  return (
    <>
      {/* Use a single AnimatePresence for all dialogs */}
      <AnimatePresence mode="wait">
        {/* Auth Required Modal */}
        {showAuthRequiredModal && (
          <AuthRequiredModal
            key="auth-required-modal"
            onClose={() => setShowAuthRequiredModal(false)}
          />
        )}

        {/* Template Dialog */}
        {showTemplateDialog && (
          <TemplateDialog
            key="template-dialog"
            onClose={() => setShowTemplateDialog(false)}
          />
        )}

        {/* Save Template Dialog */}
        {showSaveTemplateDialog && (
          <SaveTemplateDialog
            key="save-template-dialog"
            onClose={() => setShowSaveTemplateDialog(false)}
            onSaveSuccess={onSaveSuccess} // Pass the prop down
          />
        )}

        {/* Open Project Dialog */}
        {showLoadProjectDialog && (
          <LoadProjectDialog
            key="load-project-dialog"
            onClose={() => setShowLoadProjectDialog(false)}
          />
        )}

        {/* Rename Dialog */}
        {showRenameDialog && project && (
          <RenameDialog
            key="rename-dialog"
            onClose={() => setShowRenameDialog(false)}
            currentName={project.name}
            // currentDescription will be added in a later step for Item 3
          />
        )}

        {/* Save As Dialog */}
        {showSaveAsDialog && project && (
          <SaveAsDialog
            key="save-as-dialog"
            open={showSaveAsDialog}
            onClose={() => setShowSaveAsDialog(false)}
            // onSaveAs is handled internally by the dialog's handleSaveAsSubmit
            originalName={project.name}
            originalDescription={project.description}
          />
        )}

        {/* New Project Confirmation Dialog */}
        {showNewProjectConfirmDialog && (
          <ConfirmDialog
            key="new-project-confirm-dialog"
            title="Unsaved Changes"
            message="You have unsaved changes. Do you want to save them before creating a new project?"
            onClose={() => setShowNewProjectConfirmDialog(false)}
            // Rightmost button: "Save Changes" (default action)
            onConfirm={saveAndProceedWithNewProject}
            confirmText="Save Changes"
            isDanger={false} // Primary button style (blue)

            // Leftmost button: "Don't Save" (replaces old "Cancel" button behavior)
            cancelText="Don't Save" // Text for the button
            cancelAction={proceedWithNewProject} // Specific action for "Don't Save"
            cancelIsTextDanger={true} // Style text as red

            // onThirdOption and thirdOptionText are not used for this dialog instance
          />
        )}

        {/* Close Project Dialog */}
        {showCloseProjectDialog && (
          <ConfirmDialog
            key="close-project-dialog"
            title="Close Project"
            message="Are you sure you want to close this project? The canvas will be cleared and any unsaved changes will be lost."
            onClose={() => setShowCloseProjectDialog(false)}
            onConfirm={handleNewProject} // Use handleNewProject for confirm
            confirmText="Close Project"
            isDanger={true}
          />
        )}

        {/* Delete Project Dialog */}
        {showDeleteProjectDialog && project && (
          <ConfirmDialog
            key="delete-project-dialog"
            title="Delete Project"
            message={`Are you sure you want to delete "${project.name}"? This action cannot be undone.`}
            onClose={() => setShowDeleteProjectDialog(false)}
            onConfirm={handleDelete} // Use handleDelete for confirm
            confirmText="Delete Project"
            isDanger={true}
          />
        )}

        {/* Share Project Dialog */}
        {showShareDialog && project && (
          <ShareProjectDialog
            key="share-project-dialog"
            onClose={() => setShowShareDialog(false)}
          />
        )}

        {/* Keyboard Shortcuts Dialog */}
        {showKeyboardShortcutsDialog && (
          <KeyboardShortcutsDialog
            key="keyboard-shortcuts-dialog"
            onClose={() => setShowKeyboardShortcutsDialog(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
